import React, {useEffect, useRef, useState} from 'react';
import {Checkbox, Col, Form, Input, message, Row} from 'antd';
import ProForm, {ProFormInstance} from '@ant-design/pro-form';
import {WdiCardTitle, wdiCore, WdiInput, WdiModalAppResult} from '@/components/WdiControls';
import {FileUploadUtility, WdiFileUpload} from '@/components/WdiControls/WdiFileUpload';
import {PageType} from '@/services/basic/pagetype';
import {Enum} from '@/enums/system';
import {ResponseStatus} from '@/utils/request';
import {FormPageProps} from '@/components/WdiBus/FormPage';
import DataStore from '@/components/GridWidget/DataStore';
import {ApplyFooter, ApplyFooterType} from '@/components/WdiBus/ApplyFooter';
import {getPageResult, initApplyPageContext, ViewEntityProps} from '../../components/data';
import {FormSp} from '@/services/sales/sp';
import {WdiPanel} from '@/components/WdiControls/WdiPanel';
import {WarningInfoAppProps} from '@/components/WdiBus/PanelForWarning';
import SPWarningInfoApp from '@/pages/sales/sp/components/WarningInformation';
import {WdiSelect} from '@/components/WdiControls/WdiSelect';
import {VatType} from '@/services/mdm/vattype';
import {GeneralPaymentDetailsForApplyPanel} from '@/pages/sales/sp/components/detail';
import {Invoices} from '@/services/sales/financial/invoices';
import FormInvoiceForApplyPanel from '@/pages/sales/sp/components/FormInvoice';
import {BankAccount} from '@/services/mdm/bankaccount';
import {SoldTo} from '@/services/mdm/soldto';
import {DataOptions} from "@/services/basic/metadata";
import {Company} from "@/services/mdm/company";
import {WdiEditor} from "@/components/WdiControls/WdiEditor";
import {OaContractSelectApp} from "@/pages/sales/sp/components/OaContractCmpt";
import {SpPreSelectApp} from "@/pages/sales/sp/components/SpPreSelectApp";

const FormColumns = {
  VAT_TYPE_13: 1,
  VAT_TYPE_0: 2,
  PAY_TO: 3,
  IS_REVERSE_PREPAY: 4,
  PAY_TYPE: 5,
  PAY_TO_USER: 6,
  BANK_ACCOUNT: 7,
  SOLD_TO: 8,
  COMPANY: 9,
  SUPPLIER: 10,
  OA_FORM_NO: 11,
}

const FieldConfig: Record<number, Set<number>> = {
  [Enum.PaymentMethod.BILL_DEDUCT]: new Set([FormColumns.VAT_TYPE_13]),
  [Enum.PaymentMethod.FEE_POOL]: new Set([FormColumns.VAT_TYPE_13]),
  [Enum.PaymentMethod.FG_POOL]: new Set([FormColumns.VAT_TYPE_0]),
  [Enum.PaymentMethod.BUY_GIVE_POOL]: new Set([FormColumns.VAT_TYPE_0]),
  [Enum.PaymentMethod.ACCOUNTING_ACCRUAL]: new Set(),
  [Enum.PaymentMethod.ACCOUNTING_NO_ACCRUAL]: new Set(),
  [Enum.PaymentMethod.ACCOUNT_DEDUCT]: new Set([FormColumns.PAY_TO, FormColumns.SOLD_TO]),
  [Enum.PaymentMethod.TRANSFER]: new Set([
    FormColumns.IS_REVERSE_PREPAY, FormColumns.PAY_TYPE, FormColumns.BANK_ACCOUNT, FormColumns.SOLD_TO, FormColumns.COMPANY
  ]),
  [Enum.PaymentMethod.THIRD_PART]: new Set([
    FormColumns.IS_REVERSE_PREPAY, FormColumns.PAY_TYPE, FormColumns.BANK_ACCOUNT, FormColumns.OA_FORM_NO,
    FormColumns.COMPANY, FormColumns.SUPPLIER, FormColumns.PAY_TO_USER
  ]),
}

const BatchPaymentApplyApp: React.FC<FormPageProps> = (props) => {
  const formRef = useRef<ProFormInstance>();
  const [entity, setEntity] = useState<ViewEntityProps>();
  const [pageType, setPageType] = useState<any>();
  const [warningSettings, setWarningSettings] = useState<WarningInfoAppProps>({visible: false});
  const [dataSource, setDataSource] = useState<any[]>([]);
  const [invoiceDataSource, setInvoiceDataSource] = useState<API.FormInvoiceVo[]>([]);
  const [vatType, setVatType] = useState<API.VatType>();
  const [showInvoice, setShowInvoice] = useState(true);
  const [payTypeId, setPayTypeId] = useState(0);
  const [editorRef, setEditorRef] = useState<any>()
  const [isReversePrepay, setIsReversePrepay] = useState(false);
  const [paymentMethodId, setPaymentMethodId] = useState(0);

  const onDetailsChange = async (data: any[]) => {
    if (data) {
      const formSaIds = new Set(data.map((item) => String(item.formSaId)));
      const soldToCodes = new Set(data.map((item) => item.soldToCode));
      const reimburseMaterialsResponse = await DataOptions.getReimburseMaterials({
        formSaIds: Array.from(formSaIds).join(),
        paymentMethodIds: String(entity?.paymentMethodId),
        soldToCode: Array.from(soldToCodes).join(),
        companyCode: entity?.companyCode
      });
      setEntity(prevState => {
        const targetObject = {...prevState};
        targetObject.reimburseMaterials = reimburseMaterialsResponse?.data;
        targetObject.allowViewSfa = Boolean(data?.find(item => item.toSfa));
        return targetObject;
      })
    }
    setDataSource(data);
  };

  const onInvoiceChange = (data: any) => {
    setInvoiceDataSource(data);
  };

  const onVatTypeChange = (vatTypeId?: number) => {
    setVatType(undefined);
    if (vatTypeId) {
      VatType.get(vatTypeId).then(responseResult => {
        setVatType(responseResult.data);
        if (dataSource && dataSource.length > 0) {
          const newDataSource = [...dataSource];
          newDataSource.forEach(item => {
            item.vatTypeId = vatTypeId;
            item.vatTypeValue = responseResult.data.vatTypeValue;
            if (!wdiCore.Object.IsNullOrUndefined(item.amountInVat) && !wdiCore.Object.IsNullOrUndefined(item.vatTypeValue)) {
              item.amountExVat = wdiCore.Number.ToNumber(item.amountInVat / (1 + item.vatTypeValue), 2);
              item.tax = wdiCore.Number.ToNumber(item.amountInVat - item.amountExVat, 2);
            }
          })
          setDataSource(newDataSource);
        }
      })
    }
  }

  const getInvoiceStatus = (pageTypeId: number) => {
    if (Enum.PageType.PAYMENT_BATCH_BILL_DEDUCT == pageTypeId) {
      return Enum.InvoiceStatus.NON_INVOICE;
    }
    return Enum.InvoiceStatus.WITH_INVOICE;
  }

  useEffect(() => {
    (async () => {
      const params = props.data;
      if (params) {
        const result = await initApplyPageContext(params);
        if (result.code !== ResponseStatus.SUCCESS) {
          message.error(result.message);
          return;
        }
        const viewEntity: ViewEntityProps = result.data;

        if (viewEntity.formSp) {
          formRef.current?.setFieldsValue({
            vatTypeId: viewEntity.vatTypeId,
            bankAccountName: viewEntity.formSp.bankAccountName,
            reimburseMaterials: viewEntity.formSp.reimburseMaterial?.split(','),
            isReversePrepay: viewEntity.formSp.isReversePrepay,
            payTypeId: viewEntity.formSp.payTypeId,
            remark: viewEntity.formSp.remark,
            fileInfo: FileUploadUtility.parse(viewEntity.formSp.fileName, viewEntity.formSp.filePath),
            reversePrepayDetail: viewEntity.formSp.reversePrepayDetail,
          });
          setIsReversePrepay(Boolean(viewEntity.formSp.isReversePrepay));
          if (viewEntity.formSp.formSpId && viewEntity.formSp.invoiceStatus == Enum.InvoiceStatus.WITH_INVOICE) {
            Invoices.getForPayment(viewEntity.formSp.formSpId).then(responseResult => {
              let invoices = DataStore.init(responseResult.data);
              if (viewEntity.form?.relationType) {
                invoices = invoices.map(invoice => (Object.assign({}, invoice, {formInvoiceId: null})))
              }
              setInvoiceDataSource(invoices);
            })
          }
          if (viewEntity.formSp.payTypeId) {
            setPayTypeId(viewEntity.formSp.payTypeId);
          }
          if ((viewEntity.paymentMethodId == Enum.PaymentMethod.TRANSFER || viewEntity.paymentMethodId == Enum.PaymentMethod.THIRD_PART)
            && viewEntity?.formSp?.bankAccountId) {
            BankAccount.get(viewEntity?.formSp?.bankAccountId).then(res => {
              formRef.current?.setFieldsValue({
                bankAccountId: {value: res?.data?.bankAccountId, label: res?.data?.bankAccountNo},
              })
            })
          }
          if (viewEntity.formSp?.reversePrepayDetail) {
            const strArray = viewEntity.formSp?.reversePrepayDetail.split(',');
            const reversePrepayDetail = strArray.map(str => {
              const [requisitionNumber, transactionNum] = str.split(' - ');
              return {
                requisitionNumber,
                transactionNum,
              };
            });
            formRef.current?.setFieldsValue({
              reversePrepayDetail: reversePrepayDetail
            });
          }
        }

        const invoiceStatus = getInvoiceStatus(Number(viewEntity.pageTypeId));
        formRef.current?.setFieldsValue({invoiceStatus: invoiceStatus});
        setShowInvoice(invoiceStatus == Enum.InvoiceStatus.WITH_INVOICE);

        onVatTypeChange(viewEntity.vatTypeId);

        if (viewEntity.paymentMethod) {
          formRef.current?.setFieldsValue({
            paymentMethodName: viewEntity.paymentMethod.name,
          });
          setPaymentMethodId(viewEntity.paymentMethod.value)
        }
        if (viewEntity.soldToCode) {
          SoldTo.get(viewEntity.soldToCode).then((response) => {
            formRef.current?.setFieldsValue({
              soldTo: response.data.codeOrName,
            });
          });
        }
        if (viewEntity.soldToCodePayTo) {
          formRef.current?.setFieldsValue({
            soldToCodePayTo: viewEntity.soldToCodePayTo,
          });
        }
        if (viewEntity.pageTypeId) {
          PageType.get(viewEntity.pageTypeId).then((response) => {
            setPageType(response.data);
          });
        }
        if (viewEntity.companyCode) {
          Company.get(viewEntity.companyCode).then((response) => {
            formRef.current?.setFieldsValue({
              company: response.data.codeOrName,
            });
          });
        }
        if (viewEntity.supplier) {
          formRef.current?.setFieldsValue({
            supplier: viewEntity.supplier.codeOrName,
          });
        }
        if (viewEntity.payToUser) {
          formRef.current?.setFieldsValue({
            payToUser: wdiCore.String.GenerateLabelDescription(viewEntity.payToUser.staffNoOrName, viewEntity.payToUser.title),
          });
        }
        if (viewEntity.formSpDetails) {
          setDataSource(DataStore.init(viewEntity.formSpDetails));
        }
        setEntity(viewEntity);
      }
    })();
  }, [props.data]);

  useEffect(() => {
    if (props.setPageTitle && pageType && entity) {
      let title = "活动核销申请";
      if (entity.formSp) {
        title += '【编辑】'
      }
      props.setPageTitle(title);
    }
  }, [props.setPageTitle, pageType, entity]);

  return (
    <>
      <SPWarningInfoApp {...warningSettings} />
      <ProForm
        formRef={formRef}
        submitter={{
          render: () => {
            return (
              <ApplyFooter
                setLoading={props.setLoading}
                hiddenButton={[ApplyFooterType.Validate]}
                viewEntity={entity}
                submitBackUrl="/sales/sp/mypayment"
                doSubmit={async (type: number, forceSubmit: boolean) => {
                  if (!entity || !pageType) {
                    return;
                  }
                  const remark = await editorRef?.current?.getHtml?.();
                  const values = await formRef.current?.validateFieldsReturnFormatValue?.();
                  if (values && values.errorFields && values.errorFields.length) {
                    return;
                  }
                  const statusId = type === ApplyFooterType.Draft ? Enum.FormStatus.DRAFT : Enum.FormStatus.AWAITING;
                  values.remark = remark;
                  const req = getPageResult(
                    values, entity, pageType, statusId, dataSource, invoiceDataSource,
                  );
                  if (req.code !== ResponseStatus.SUCCESS) {
                    message.error(req.message);
                    return;
                  }
                  props?.setLoading?.(true);
                  return await FormSp.Buttons.apply({
                    forceSubmit: forceSubmit,
                    ...req.data,
                  });
                }}
                del={async () => {
                  if (!entity || !entity.formId) {
                    return;
                  }
                  return await FormSp.Buttons.del(entity.formId);
                }}
                showWarning={(result: WdiModalAppResult, submitCallback: any) => {
                  setWarningSettings({
                    visible: true,
                    title: '警示信息',
                    wdiPanelList: result.data,
                    onClose: () => {
                      setWarningSettings({visible: false});
                    },
                    onSave: async () => {
                      return await submitCallback();
                    },
                  });
                }}
              />
            );
          },
        }}
      >
        <WdiPanel title={<WdiCardTitle iconType={Enum.IconType.FORM_SP} title="活动核销单信息"/>}>
          <Row gutter={[16, 0]}>
            {FieldConfig[paymentMethodId]?.has(FormColumns.VAT_TYPE_13) && (
              <Col className="gutter-row" span={4}>
                <Form.Item name="vatTypeId" label="可抵扣税率类型(普票为0)"
                           rules={[{required: true, message: '可抵扣税率类型不能为空'}]}>
                  <WdiSelect
                    {...{
                      placeholder: '请选择可抵扣税率类型',
                      disabled: true,
                      dataLoading: async (params) => {
                        const response = await VatType.getVAT13();
                        const vatType = response.data;
                        formRef.current?.setFieldsValue({vatTypeId: vatType.vatTypeId,});
                        setVatType(vatType);
                        return [{value: vatType.vatTypeId, label: vatType.vatTypeName}];
                      },
                    }}
                  />
                </Form.Item>
              </Col>
            )}
            {FieldConfig[paymentMethodId]?.has(FormColumns.VAT_TYPE_0) && (
              <Col className="gutter-row" span={4}>
                <Form.Item name="vatTypeId" label="可抵扣税率类型(普票为0)"
                           rules={[{required: true, message: '可抵扣税率类型不能为空'}]}>
                  <WdiSelect
                    {...{
                      placeholder: '请选择可抵扣税率类型',
                      disabled: true,
                      dataLoading: async (params) => {
                        const response = await VatType.getVAT0();
                        const vatType = response.data;
                        formRef.current?.setFieldsValue({vatTypeId: vatType.vatTypeId,});
                        setVatType(vatType);
                        return [{value: vatType.vatTypeId, label: vatType.vatTypeName}];
                      },
                    }}
                  />
                </Form.Item>
              </Col>
            )}
            {!FieldConfig[paymentMethodId]?.has(FormColumns.VAT_TYPE_13) && !FieldConfig[paymentMethodId]?.has(FormColumns.VAT_TYPE_0) && (
              <Col className="gutter-row" span={4}>
                <Form.Item name="vatTypeId" label="可抵扣税率类型(普票为0)"
                           rules={[{required: true, message: '可抵扣税率类型不能为空'}]}>
                  <WdiSelect
                    {...{
                      placeholder: '请选择可抵扣税率类型',
                      onChange: (value) => {
                        onVatTypeChange(value);
                      },
                      dataLoading: async (params) => {
                        const response = await VatType.search(
                          Object.assign({isActive: true}, params),
                        );
                        return response.data.map((item) => {
                          return {value: item.vatTypeId, label: item.vatTypeName};
                        });
                      },
                    }}
                  />
                </Form.Item>
              </Col>
            )}
            {FieldConfig[paymentMethodId]?.has(FormColumns.PAY_TO) && (
              <Col className="gutter-row" span={4}>
                <Form.Item
                  name="soldToCodePayTo"
                  label="付款客户"
                  rules={[{required: true, message: '付款客户不能为空'}]}
                >
                  <WdiSelect
                    {...{
                      placeholder: '请选择付款客户',
                      showSearch: true,
                      remote: true,
                      dataLoading: async (params) => {
                        if (entity?.soldToCode) {
                          const soldToResp = await SoldTo.get(entity?.soldToCode);
                          const soldTo = soldToResp.data;
                          const response = await SoldTo.ajaxSearch(
                            Object.assign({
                              custSysCode: soldTo.custSysCode,
                              companyCode: soldTo.companyCode,
                            }, params),
                          );
                          const soldTos = [...response.data];
                          if (soldTos.findIndex(item => item.soldToCode == soldTo.soldToCode) < 0) {
                            soldTos.push(soldTo)
                          }
                          if (soldTos.length === 1) {
                            formRef.current?.setFieldsValue({
                              soldToCodePayTo: {value: soldTos[0].soldToCode, label: soldTos[0].codeOrName}
                            });
                          }
                          return soldTos?.map((item) => ({
                            value: item.soldToCode,
                            label: item.codeOrName,
                          }));
                        } else {
                          return [];
                        }
                      },
                      reloadKey: entity?.soldToCode,
                    }}
                  />
                </Form.Item>
              </Col>
            )}
            {FieldConfig[paymentMethodId]?.has(FormColumns.IS_REVERSE_PREPAY) && (
              <Col className="gutter-row" span={4}>
                <Form.Item name="isReversePrepay" label="是否冲销预付" valuePropName="checked">
                  <Checkbox onChange={(e) => {
                    setIsReversePrepay(e.target.checked)
                    if (!e.target.checked) {
                      formRef.current?.setFieldsValue({
                        reversePrepayDetail: null
                      });
                    }
                  }}/>
                </Form.Item>
              </Col>
            )}
            {FieldConfig[paymentMethodId]?.has(FormColumns.IS_REVERSE_PREPAY) && isReversePrepay && (
              <Col className="gutter-row" span={4}>
                <Form.Item name="reversePrepayDetail" label="冲销的预付单"
                           rules={[{
                             required: FieldConfig[paymentMethodId]?.has(FormColumns.IS_REVERSE_PREPAY) && isReversePrepay,
                             message: "冲销的预付单不能为空!"
                           }]}>
                  <SpPreSelectApp
                    filterParams={{
                      paymentMethodId: entity?.paymentMethodId,
                      companyCode: entity?.companyCode,
                      payToUserId: entity?.payToUserId,
                      supplierId: entity?.supplierId,
                      soldToCode: entity?.soldToCode,
                    }}
                  />
                </Form.Item>
              </Col>
            )}
            {FieldConfig[paymentMethodId]?.has(FormColumns.PAY_TYPE) && (
              <Col className="gutter-row" span={4}>
                <Form.Item name="payTypeId" label="支付类型" rules={[{required: true, message: "支付类型不能为空!"}]}>
                  <WdiSelect
                    {...{
                      placeholder: '请选择支付类型',
                      dataLoading: async (params) => {
                        const response = await DataOptions.search(Object.assign({
                          typeGroup: 106,
                          isActive: true
                        }, params));
                        return response.data.map((item) => {
                          return {value: item.value, label: item.name};
                        });
                      },
                      onChange: (value) => {
                        setPayTypeId(value);
                        formRef.current?.setFieldsValue({bankAccountId: null, bankAccountName: null});

                      },
                    }}
                  />
                </Form.Item>
              </Col>
            )}
            {FieldConfig[paymentMethodId]?.has(FormColumns.BANK_ACCOUNT) && (
              <Col className="gutter-row" span={4}>
                <Form.Item name="bankAccountId" label="银行账号"
                           rules={[{required: payTypeId == Enum.PayType.CASH, message: '银行账号不能为空'}]}>
                  <WdiSelect
                    {...{
                      placeholder: '请选择银行账号',
                      showSearch: true,
                      remote: true,
                      dataLoading: async (params) => {
                        const param: API.BankAccountFilter = {isActive: true};
                        if (entity?.paymentMethodId == Enum.PaymentMethod.TRANSFER) {
                          param.soldToCode = entity?.soldToCode;
                        } else if (entity?.payToUserId) {
                          param.userId = entity?.payToUserId;
                        } else {
                          param.supplierId = entity?.supplierId;
                          param.companyCode = entity?.companyCode;
                        }
                        const response = await BankAccount.ajaxSearch(Object.assign(param, params,));
                        return response.data.map((item) => ({value: item.bankAccountId, label: item.bankAccountNo}));
                      },
                      onChange: (value) => {
                        formRef.current?.setFieldsValue({bankAccountName: null});
                        if (value) {
                          BankAccount.get(value).then((res) => {
                            formRef.current?.setFieldsValue({
                              bankAccountName: res?.data?.bankAccountName,
                            });
                          });
                        }
                      },
                      reloadKey: `${entity?.soldToCode}-${entity?.payToUserId}-${entity?.supplierId}-${entity?.companyCode}`,
                    }}
                  />
                </Form.Item>
              </Col>
            )}
            {FieldConfig[paymentMethodId]?.has(FormColumns.BANK_ACCOUNT) && (
              <Col className="gutter-row" span={4}>
                <Form.Item name="bankAccountName" label="户名" rules={[{required: payTypeId == Enum.PayType.CASH}]}>
                  <WdiInput/>
                </Form.Item>
              </Col>
            )}
            {FieldConfig[paymentMethodId]?.has(FormColumns.OA_FORM_NO) && (
              <Col className="gutter-row" span={4}>
                <Form.Item name="oaFormNo" label="OA合同号">
                  <OaContractSelectApp/>
                </Form.Item>
              </Col>
            )}
            <Col className="gutter-row" span={4}>
              <Form.Item name="paymentMethodName" label="支付方式">
                <Input readOnly/>
              </Form.Item>
            </Col>
            {FieldConfig[paymentMethodId]?.has(FormColumns.SOLD_TO) && (
              <Col className="gutter-row" span={4}>
                <Form.Item name="soldTo" label="客户">
                  <WdiInput readOnly/>
                </Form.Item>
              </Col>
            )}
            {FieldConfig[paymentMethodId]?.has(FormColumns.COMPANY) && (
              <Col className="gutter-row" span={4}>
                <Form.Item name="company" label="公司">
                  <WdiInput readOnly/>
                </Form.Item>
              </Col>
            )}
            {FieldConfig[paymentMethodId]?.has(FormColumns.SUPPLIER) && entity?.supplier && (
              <Col className="gutter-row" span={4}>
                <Form.Item name="supplier" label="供应商">
                  <WdiInput readOnly/>
                </Form.Item>
              </Col>
            )}
            {FieldConfig[paymentMethodId]?.has(FormColumns.PAY_TO_USER) && entity?.payToUser && (
              <Col className="gutter-row" span={4}>
                <Form.Item name="payToUser" label="员工">
                  <WdiInput readOnly/>
                </Form.Item>
              </Col>
            )}
            <Col className="gutter-row" span={4} hidden={true}>
              <Form.Item name="invoiceStatus" label="发票状态">
                <WdiInput readOnly/>
              </Form.Item>
            </Col>
            <Col className="gutter-row" span={16}>
              <Form.Item name="reimburseMaterials" label="核销支持文件">
                <Checkbox.Group
                  options={(entity?.reimburseMaterials || []).map((item) => ({
                    label: item.name,
                    value: String(item.value),
                  }))}
                ></Checkbox.Group>
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={[16, 24]}>
            <Col className="gutter-row" span={16}>
              <Form.Item name="remark" label="备注">
                <WdiEditor setRef={setEditorRef}/>
              </Form.Item>
            </Col>
            <Col className="gutter-ferow" span={8}>
              <Form.Item
                name="fileInfo"
                label="附件"
                valuePropName="fileList"
                getValueFromEvent={(e: any) => {
                  return e.fileList || [];
                }}
              >
                <WdiFileUpload leftTopInfo="请选择文件并上传(单个文件不能超500M)"/>
              </Form.Item>
            </Col>
          </Row>
        </WdiPanel>
        <GeneralPaymentDetailsForApplyPanel
          data={{
            allowViewSfa: entity?.allowViewSfa,
            formSp: {
              pageTypeId: entity?.pageTypeId,
              pageGroupId: entity?.pageGroupId,
              formSaId: entity?.formSaId,
              vatTypeId: vatType?.vatTypeId,
              paymentMethodId: entity?.paymentMethodId,
              soldToCode: entity?.soldToCode,
              companyCode: entity?.companyCode,
              supplierId: entity?.supplierId,
            },
            vatType: vatType,
            ecFieldConfigs: entity?.ecFieldConfigs,
          }}
          dataSource={dataSource}
          onChange={onDetailsChange}
        />
        {showInvoice && (
          <FormInvoiceForApplyPanel
            data={{
              vatType: vatType,
              details: dataSource,
              soldToCode: entity?.soldToCode,
              paymentMethodId: entity?.paymentMethodId,
              companyCode: entity?.companyCode,
              supplierId: entity?.supplierId,
              payToUserId: entity?.payToUserId,
            }}
            dataSource={invoiceDataSource}
            onChange={onInvoiceChange}
          />
        )}
      </ProForm>
    </>
  );
};
export default BatchPaymentApplyApp;
